// src/pages/admin/AIInterfaceTestPage.tsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Activity, Shield, Terminal, Check, X } from 'lucide-react';
import { testGeminiConnection, syncAIModels } from '../../lib/api';
import type { GeminiConnectionResponse, AIModelsSyncResponse } from '../../lib/api';
import { Radio, RadioGroup } from '../../components/ui/Radio';
import LogoutButton from '../../components/common/LogoutButton';

type HealthStatus = 'optimal' | 'degraded' | 'critical' | 'offline' | 'loading';

interface HeartbeatRecord {
  timestamp: Date;
  latency: number;
  status: 'success' | 'anomaly' | 'error';
  message: string;
}

const STATUS_COLORS: Record<HealthStatus, { primary: string; secondary: string; glow: string; label: string }> = {
  loading: { primary: '#00BFFF', secondary: '#87CEEB', glow: '0 0 15px #00BFFF', label: '连接中' },
  optimal: { primary: '#39FF14', secondary: '#00ff88', glow: '0 0 15px #39FF14', label: '极速' },
  degraded: { primary: '#FCEE09', secondary: '#ffcc00', glow: '0 0 10px #FCEE09', label: '降级' },
  critical: { primary: '#FF8C00', secondary: '#ff6600', glow: '0 0 10px #FF8C00', label: '重载' },
  offline: { primary: '#FF007F', secondary: '#ff0044', glow: '0 0 15px #FF007F', label: '离线' }
};

const STATUS_MESSAGES: Record<HealthStatus, { title: string; subtitle: string; emoji: string }> = {
  loading: { title: '正在打卡中...', subtitle: '接口正在赶来的路上，比我早八通勤还努力！', emoji: '⏳' },
  optimal: { title: '老板，这活儿干得漂亮！', subtitle: '接口响应比我摸鱼还快，KPI稳了！', emoji: '😎' },
  degraded: { title: '摸鱼被发现了...', subtitle: '接口开始偷懒了，人在工位魂在床。', emoji: '😅' },
  critical: { title: '这班没法上了！', subtitle: '接口比我连上七天班还虚，随时准备提桶跑路！', emoji: '🆘' },
  offline: { title: '已离职，勿念', subtitle: '接口彻底跑路了！比我裸辞还干脆！', emoji: '💀' }
};

const HeartbeatIcon: React.FC<{ status: HealthStatus }> = ({ status }) => {
  const colors = STATUS_COLORS[status];
  const isAlive = status !== 'offline' && status !== 'loading';
  
  return (
    <div className="relative flex items-center justify-center">
      <svg 
        className="w-10 h-10" 
        viewBox="0 0 24 24" 
        style={{ 
          filter: `drop-shadow(${colors.glow})`,
          animation: isAlive ? 'heartbeat 1s ease-in-out infinite' : 'none'
        }}
      >
        <path 
          d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
          fill={colors.primary}
          stroke={colors.secondary}
          strokeWidth="0.5"
        />
      </svg>
    </div>
  );
};

const CoreReactor: React.FC<{ status: HealthStatus; successRate: number }> = ({ status, successRate }) => {
  const colors = STATUS_COLORS[status];
  
  return (
    <div className="relative flex items-center justify-center w-144 h-144">
      <svg className="w-128 h-128" viewBox="0 0 200 200">
        <defs>
          <filter id={`glow-${status}`}>
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
          <linearGradient id="reactorGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={colors.primary} />
            <stop offset="100%" stopColor={colors.secondary} />
          </linearGradient>
        </defs>
        <circle cx="100" cy="100" r="90" fill="none" stroke={colors.primary} strokeWidth="2" opacity="0.3" className="animate-ping" />
        <circle cx="100" cy="100" r="80" fill="none" stroke={colors.primary} strokeWidth="4" strokeLinecap="round" strokeDasharray="20 10" filter={`url(#glow-${status})`}
          style={{ transformOrigin: 'center', animation: status === 'offline' || status === 'loading' ? 'none' : `spin 2s linear infinite` }} />
        <circle cx="100" cy="100" r="50" fill="none" stroke={colors.primary} strokeWidth="3" opacity="0.5" filter={`url(#glow-${status})`} />
        <circle cx="100" cy="100" r="30" fill={`url(#reactorGradient)`} filter={`url(#glow-${status})`} />
        <text x="100" y="108" textAnchor="middle" fill="#fff" fontSize="28" fontWeight="bold" fontFamily="monospace">
          {status === 'loading' ? '...' : `${Math.round(successRate)}%`}
        </text>
      </svg>
    </div>
  );
};

const PulseWave: React.FC<{ latency: number; history: number[] }> = ({ latency, history }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const width = canvas.width;
    const height = canvas.height;
    
    ctx.fillStyle = '#050505';
    ctx.fillRect(0, 0, width, height);
    
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 1;
    for (let i = 0; i < width; i += 20) { ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i, height); ctx.stroke(); }
    for (let i = 0; i < height; i += 20) { ctx.beginPath(); ctx.moveTo(0, i); ctx.lineTo(width, i); ctx.stroke(); }
    
    if (history.length < 2) return;
    
    const maxLatency = Math.max(...history, 3000);
    const points = history.map((lat, i) => ({
      x: (i / (history.length - 1)) * width,
      y: height - (lat / maxLatency) * height * 0.8 - height * 0.1
    }));
    
    const gradient = ctx.createLinearGradient(0, 0, width, 0);
    gradient.addColorStop(0, '#39FF1488');
    gradient.addColorStop(0.5, '#00ffcc88');
    gradient.addColorStop(1, '#FF007F88');
    
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      const xc = (points[i].x + points[i - 1].x) / 2;
      const yc = (points[i].y + points[i - 1].y) / 2;
      ctx.quadraticCurveTo(points[i - 1].x, points[i - 1].y, xc, yc);
    }
    ctx.lineTo(points[points.length - 1].x, points[points.length - 1].y);
    ctx.strokeStyle = gradient;
    ctx.lineWidth = 2;
    ctx.shadowColor = '#39FF14';
    ctx.shadowBlur = 8;
    ctx.stroke();
    ctx.shadowBlur = 0;
    
    if (latency > 0) {
      const lastPoint = points[points.length - 1];
      ctx.beginPath();
      ctx.arc(lastPoint.x, lastPoint.y, 5, 0, Math.PI * 2);
      ctx.fillStyle = latency < 300 ? '#39FF14' : latency < 1000 ? '#FCEE09' : latency < 2000 ? '#FF8C00' : '#FF007F';
      ctx.shadowColor = ctx.fillStyle as string;
      ctx.shadowBlur = 10;
      ctx.fill();
    }
  }, [latency, history]);
  
  return (
    <div className="relative bg-black rounded border border-gray-800 overflow-hidden">
      <canvas ref={canvasRef} width={300} height={100} className="w-full h-28" />
      <div className="absolute top-1 left-2 flex items-center gap-1 text-gray-500 text-sm font-mono">
        <Activity size={14} /><span>PULSE</span>
      </div>
      <div className="absolute top-1 right-2 text-sm font-mono" style={{ color: latency < 300 ? '#39FF14' : latency < 1000 ? '#FCEE09' : latency < 2000 ? '#FF8C00' : '#FF007F' }}>
        {latency > 0 ? `${latency.toFixed(0)}ms` : '--'}
      </div>
    </div>
  );
};

const ShieldBar: React.FC<{ records: HeartbeatRecord[] }> = ({ records }) => {
  const last10 = records.slice(-10);
  const successCount = last10.filter(r => r.status === 'success').length;
  
  return (
    <div className="bg-black/50 rounded border border-gray-800 p-2">
      <div className="flex items-center gap-1 mb-1 text-gray-500 text-sm font-mono">
        <Shield size={14} />
        <span>SHIELD</span>
        <span className="ml-auto" style={{ color: STATUS_COLORS[successCount >= 8 ? 'optimal' : successCount >= 5 ? 'degraded' : 'offline'].primary }}>
          {successCount}/{last10.length || 0}
        </span>
      </div>
      <div className="flex gap-0.5">
        {Array.from({ length: 10 }).map((_, i) => {
          const record = last10[i];
          const isActive = record !== undefined;
          const isSuccess = record?.status === 'success';
          return (
            <div key={i} className="flex-1 h-5 rounded-sm transition-all duration-300"
              style={{
                backgroundColor: isActive ? (isSuccess ? '#39FF14' : record.status === 'anomaly' ? '#FCEE09' : '#FF007F') : '#1a1a1a',
                boxShadow: isActive && isSuccess ? '0 0 6px #39FF14' : 'none',
                opacity: isActive ? 1 : 0.3
              }} />
          );
        })}
      </div>
    </div>
  );
};

const TerminalLog: React.FC<{ logs: string[] }> = ({ logs }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (containerRef.current) {
      requestAnimationFrame(() => {
        if (containerRef.current) {
          containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
      });
    }
  }, [logs]);
  
  return (
    <div className="bg-black/80 rounded border border-gray-800 overflow-hidden h-full">
      <div className="flex items-center gap-1 px-2 py-1 bg-gray-900/50 border-b border-gray-800 text-gray-500 text-sm font-mono">
        <Terminal size={14} /><span>TERMINAL LOG</span>
      </div>
      <div ref={containerRef} className="h-64 overflow-y-auto p-2 font-mono text-sm space-y-0.5">
        {logs.length === 0 && <div className="text-gray-600">// Waiting...</div>}
        {logs.map((log, i) => {
          const isError = log.includes('ERROR') || log.includes('FAILED');
          const isSuccess = log.includes('OK');
          return (
            <div key={i} className={`${isError ? 'text-red-400' : isSuccess ? 'text-green-400' : 'text-gray-400'}`}>
              <span className="text-gray-600">$</span> {log}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const AIInterfaceTestPage: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<HealthStatus>('loading');
  const [records, setRecords] = useState<HeartbeatRecord[]>([]);
  const [logs, setLogs] = useState<string[]>([]);
  const [currentLatency, setCurrentLatency] = useState(0);
  const [isSwitching, setIsSwitching] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [currentThird, setCurrentThird] = useState<'gemini' | 'ucloud'>('gemini');
  const intervalRef = useRef<number | null>(null);
  const switchingRef = useRef(false);
  
  const addLog = useCallback((message: string) => {
    const timestamp = new Date().toLocaleTimeString('en-US', { hour12: false });
    setLogs(prev => [...prev.slice(-30), `[${timestamp}] ${message}`]);
  }, []);
  
  const getHealthStatusFromRecords = (recs: HeartbeatRecord[]): HealthStatus => {
    const recentRecords = recs.slice(-10);
    if (recentRecords.length === 0) return 'loading';
    const totalRequests = recentRecords.length;
    const errorCount = recentRecords.filter(r => r.status === 'error').length;
    const anomalyCount = recentRecords.filter(r => r.status === 'anomaly').length;
    const successCount = recentRecords.filter(r => r.status === 'success').length;
    const successRate = (successCount / totalRequests) * 100;
    if (errorCount >= Math.ceil(totalRequests * 0.5) || successRate < 20) return 'offline';
    if (errorCount >= Math.ceil(totalRequests * 0.3) || successRate < 50) return 'critical';
    if (anomalyCount >= Math.ceil(totalRequests * 0.3) || successRate < 80) return 'degraded';
    return 'optimal';
  };
  
  const calculateSuccessRate = useCallback(() => {
    const recentRecords = records.slice(-10);
    if (recentRecords.length === 0) return 0;
    return (recentRecords.filter(r => r.status === 'success').length / recentRecords.length) * 100;
  }, [records]);
  
  const heartbeat = useCallback(async () => {
    try {
      const startTime = performance.now();
      const response = await testGeminiConnection();
      const latency = performance.now() - startTime;
      
      const record: HeartbeatRecord = { timestamp: new Date(), latency, status: response.status, message: response.message };
      setCurrentLatency(latency);
      if (!switchingRef.current && response.current_third && (response.current_third === 'gemini' || response.current_third === 'ucloud')) {
        setCurrentThird(response.current_third);
      }
      setRecords(prev => {
        const newRecords = [...prev.slice(-59), record];
        setStatus(getHealthStatusFromRecords(newRecords));
        return newRecords;
      });
      
      if (response.status === 'success') addLog(`[${response.current_third?.toUpperCase() || '?'}] OK (${latency.toFixed(0)}ms)`);
      else if (response.status === 'anomaly') addLog(`[${response.current_third?.toUpperCase() || '?'}] ANOMALY (${latency.toFixed(0)}ms)`);
      else addLog(`[${response.current_third?.toUpperCase() || '?'}] FAILED - ${response.message}`);
    } catch (error: any) {
      setCurrentLatency(0);
      setRecords(prev => {
        const newRecords = [...prev.slice(-59), { timestamp: new Date(), latency: 0, status: 'error' as const, message: error.message || 'Connection failed' }];
        setStatus(getHealthStatusFromRecords(newRecords));
        return newRecords;
      });
      addLog(`ERROR: ${error.message || 'Network error'}`);
    }
  }, [addLog]);
  
  useEffect(() => {
    addLog('System initialized...');
    heartbeat();
    intervalRef.current = window.setInterval(heartbeat, 20000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);
  
  const handleSwitchModel = async (provider: 'gemini' | 'ucloud') => {
    setIsSwitching(true);
    switchingRef.current = true;
    setSyncMessage(null);
    addLog(`Switching to ${provider.toUpperCase()}...`);
    try {
      const response = await syncAIModels(provider);
      setSyncMessage(response.message);
      if (response.current_third && (response.current_third === 'gemini' || response.current_third === 'ucloud')) {
        setCurrentThird(response.current_third);
      }
      addLog(`Done: ${response.message}`);
    } catch (error: any) {
      setSyncMessage(`Error: ${error.message}`);
      addLog(`Failed: ${error.message}`);
    } finally {
      setIsSwitching(false);
      setTimeout(() => {
        switchingRef.current = false;
      }, 5000);
    }
  };
  
  const successRate = calculateSuccessRate();
  const latencyHistory = records.map(r => r.latency);
  
  return (
    <div className="h-screen bg-[#050505] text-white overflow-hidden relative flex flex-col">
      <div className="absolute inset-0 pointer-events-none bg-[linear-gradient(rgba(0,255,0,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,255,0,0.02)_1px,transparent_1px)] bg-[size:40px_40px]" />
      
      <header className="relative z-10 flex justify-between items-center px-4 py-2 border-b border-gray-800 bg-gray-900/50">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/admin')} className="flex items-center gap-1 px-3 py-1.5 bg-white/10 border border-white/20 rounded text-sm hover:bg-white/20 transition-all">
            <ArrowLeft size={16} /><span>返回</span>
          </button>
          <h1 className="text-xl font-bold tracking-wider">AI 接口监控中心</h1>
        </div>
        <LogoutButton />
      </header>
      
      <div className="relative z-10 flex-1 p-4 overflow-hidden">
        <div className="grid grid-cols-12 gap-4 h-full">
          <div className="col-span-3 flex flex-col gap-3">
            <div className={`bg-gray-900/50 backdrop-blur rounded-lg p-2 border ${status === 'offline' ? 'border-red-500/50 animate-pulse' : status === 'loading' ? 'border-cyan-500/50' : 'border-gray-700'} flex flex-col items-center justify-center`}>
              <CoreReactor status={status} successRate={successRate} />
              <div className="flex items-center gap-3 mt-2">
                <HeartbeatIcon status={status} />
                <span 
                  className="text-2xl font-bold tracking-widest"
                  style={{ 
                    color: STATUS_COLORS[status].primary, 
                    textShadow: STATUS_COLORS[status].glow 
                  }}
                >
                  {STATUS_COLORS[status].label}
                </span>
              </div>
            </div>
            
            <div className="bg-gray-900/50 backdrop-blur rounded-lg p-3 border border-gray-700 text-center">
              <div className="text-4xl">{STATUS_MESSAGES[status].emoji}</div>
              <div className="text-lg font-bold" style={{ color: STATUS_COLORS[status].primary }}>{STATUS_MESSAGES[status].title}</div>
              <div className="text-sm text-gray-400">{STATUS_MESSAGES[status].subtitle}</div>
            </div>
            
            <ShieldBar records={records} />
          </div>
          
          <div className="col-span-9 flex flex-col gap-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-gray-900/50 backdrop-blur rounded-lg p-2 border border-gray-700">
                <PulseWave latency={currentLatency} history={latencyHistory} />
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-gray-900/50 backdrop-blur rounded-lg p-2 border border-gray-700 text-center">
                  <div className="text-sm text-gray-500">总请求</div>
                  <div className="text-3xl font-mono">{records.length}</div>
                </div>
                <div className="bg-gray-900/50 backdrop-blur rounded-lg p-2 border border-gray-700 text-center">
                  <div className="text-sm text-gray-500">成功</div>
                  <div className="text-3xl font-mono text-green-400">{records.filter(r => r.status === 'success').length}</div>
                </div>
                <div className="bg-gray-900/50 backdrop-blur rounded-lg p-2 border border-gray-700 text-center">
                  <div className="text-sm text-gray-500">异常</div>
                  <div className="text-3xl font-mono text-yellow-400">{records.filter(r => r.status === 'anomaly').length}</div>
                </div>
                <div className="bg-gray-900/50 backdrop-blur rounded-lg p-2 border border-gray-700 text-center">
                  <div className="text-sm text-gray-500">失败</div>
                  <div className="text-3xl font-mono text-red-400">{records.filter(r => r.status === 'error').length}</div>
                </div>
              </div>
            </div>
            
            <div className="flex-1 grid grid-cols-4 gap-3">
              <div className="bg-gray-900/50 backdrop-blur rounded-lg p-3 border border-gray-700">
                <h3 className="text-sm font-semibold mb-2 text-gray-400">接口切换</h3>
                <RadioGroup value={currentThird} onChange={(value) => { setCurrentThird(value as 'gemini' | 'ucloud'); handleSwitchModel(value as 'gemini' | 'ucloud'); }} disabled={isSwitching} className="space-y-1">
                  <Radio value="gemini" label="Gemini" />
                  <Radio value="ucloud" label="UCloud" />
                </RadioGroup>
                {syncMessage && (
                  <div className={`mt-2 p-2 rounded text-sm flex items-center gap-1 ${syncMessage.includes('Error') ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-green-500/20 text-green-400 border border-green-500/30'}`}>
                    {syncMessage.includes('Error') ? <X size={14} /> : <Check size={14} />}
                    {syncMessage}
                  </div>
                )}
              </div>
              
              <div className="col-span-3">
                <TerminalLog logs={logs} />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="relative z-10 text-center text-gray-600 text-sm font-mono py-1 border-t border-gray-800 bg-gray-900/30">
        Heartbeat: 20s | Status: {status.toUpperCase()} | Provider: {currentThird.toUpperCase()}
      </div>
      
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes heartbeat {
          0%, 100% { transform: scale(1); }
          15% { transform: scale(1.15); }
          30% { transform: scale(1); }
          45% { transform: scale(1.1); }
          60% { transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default AIInterfaceTestPage;
